import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useBetPlaceMutation, useGetStakesQuery } from '@/store/api/betApi';
import { clearBet, setBetRate, setStakeAmount } from '@/store/slices/betSlice';
import { setEditStake } from '@/store';
import { useMarketExposureData } from '@/hooks/useMarketExposureData';
import { PlaceBetPayload } from '@/types/Bet';
import { formatNumber, isGuestUser } from '@/utils';
import { Icons } from '@/utils/Icons';

type BetSlipFormProps = {
  /** Initial state of the "Accept odds change" checkbox */
  defaultAcceptOddsChange?: boolean;
  onClose?: () => void;
};

const exposureClass = (value?: number) =>
  Number(value) > 0 ? 'text-success-emerald' : Number(value) < 0 ? 'text-danger-red' : '';

/**
 * Dark bet slip for the current `state.bet.selectedBet`: odds stepper, stake input,
 * quick-stake chips, projected profit, runner exposure and the place-bet actions.
 * Used by the desktop BettingContainer and the mobile PlaceBetModal.
 */
const BetSlipForm = ({ defaultAcceptOddsChange = false, onClose }: BetSlipFormProps) => {
  const dispatch = useAppDispatch();
  const { eventId } = useParams();
  const betDetails = useAppSelector((state) => state.bet);
  const { selectedBet } = betDetails;
  // Start from the stored stake: cashout presets it before the slip mounts, and starting at 0
  // would sync that 0 back to the store and wipe the hedge stake.
  const [stake, setStake] = useState(betDetails.stake);
  // "Accept odds change" checkbox is commented out below; the bet still sends the default
  // (on in the mobile popup, off in the desktop slip).
  // const [acceptOddsChange, setAcceptOddsChange] = useState(defaultAcceptOddsChange);
  const acceptOddsChange = defaultAcceptOddsChange;

  const [betPlace, { isLoading }] = useBetPlaceMutation();
  const { data: stakeData } = useGetStakesQuery(undefined, { skip: isGuestUser() });
  const { getBooksetBySelectionId } = useMarketExposureData(
    selectedBet?.marketId ?? undefined,
    eventId
  );

  /** Sync stake to redux (drives projected exposure on the market rows) */
  useEffect(() => {
    dispatch(setStakeAmount(stake));
  }, [stake]);

  /** Pick up the stake when a new selection lands or it is set externally (e.g. via cashout) */
  useEffect(() => {
    setStake(betDetails.stake);
  }, [selectedBet?.marketId, selectedBet?.selectionId, selectedBet?.betOn, betDetails.stake]);

  if (!selectedBet) return null;

  const isBackBet = selectedBet.betOn?.toLowerCase() === 'back';
  const isFancy = selectedBet.marketType?.toLowerCase() === 'fancy';
  const isBookmaker = selectedBet.marketCategory?.toLowerCase() === 'bookmaker';
  const profit = isBackBet
    ? isBookmaker
      ? formatNumber((Number(selectedBet.rate) * stake) / 100)
      : formatNumber((Number(selectedBet.rate) - 1) * stake)
    : stake;

  const reset = () => {
    dispatch(clearBet());
    setStake(0);
    onClose?.();
  };

  const changeRate = (delta: number) =>
    dispatch(setBetRate(Number(Math.max(0, Number(selectedBet.rate ?? 0) + delta).toFixed(2))));

  const handleBetPlace = async () => {
    const {
      marketId,
      marketName,
      marketCategory,
      marketType,
      selectionId,
      runnerName,
      betOn,
      rate,
      fancyPercentage,
    } = selectedBet;
    const betEventId = selectedBet.eventId;

    if (!betEventId || !marketId || !marketName || !selectionId || !runnerName || !rate || !betOn)
      return;

    const payload: PlaceBetPayload = {
      eventId: betEventId,
      marketId,
      marketName,
      marketCategory: marketCategory!,
      marketType: marketType!,
      selectionId,
      runnerName,
      betOn,
      stake,
      rate,
      fancyPercentage,
      acceptOddsChange,
    };

    try {
      await betPlace(payload).unwrap();
      reset();
    } catch (error) {
      console.log(error);
    }
  };

  const fieldClass =
    'h-10 w-full rounded-lg border border-shell-border bg-shell-raised text-center text-sm font-semibold text-shell-fg outline-none focus:border-gold';

  return (
    <div
      className={`overflow-hidden rounded-xl border-t-4 bg-shell-panel ${isBackBet ? 'border-back' : 'border-lay'}`}
    >
      {/* Selection */}
      <div className="flex items-start justify-between gap-3 px-4 pt-3">
        <div className="min-w-0">
          <span
            className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-odds ${
              isBackBet ? 'bg-back' : 'bg-lay'
            }`}
          >
            {isFancy ? (isBackBet ? 'Yes' : 'No') : selectedBet.betOn}
          </span>
          <div className="mt-1 truncate text-sm font-semibold text-shell-fg">
            {selectedBet.runnerName}
          </div>
          <div className="truncate text-xs text-shell-muted">{selectedBet.marketName}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[11px] text-shell-muted">Profit</div>
          <div className="text-base font-bold text-success-emerald">{isFancy ? '-' : profit}</div>
        </div>
      </div>

      {/* Odds + stake */}
      <div className="grid grid-cols-2 gap-3 px-4 pt-3">
        <label className="block">
          <span className="mb-1 block text-[11px] text-shell-muted">Odds</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => changeRate(-0.01)}
              className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-shell-raised text-shell-fg hover:text-gold"
              aria-label="Decrease odds"
            >
              −
            </button>
            <input
              value={selectedBet.rate ? selectedBet.rate : ''}
              onChange={(e) => {
                if (/^\d*\.?\d*$/.test(e.target.value))
                  dispatch(setBetRate(Number(e.target.value)));
              }}
              inputMode="decimal"
              className={fieldClass}
            />
            <button
              type="button"
              onClick={() => changeRate(0.01)}
              className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-shell-raised text-shell-fg hover:text-gold"
              aria-label="Increase odds"
            >
              +
            </button>
          </div>
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] text-shell-muted">Stake</span>
          <input
            value={stake || ''}
            placeholder="0"
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) setStake(value === '' ? 0 : Number(value));
            }}
            inputMode="numeric"
            className={fieldClass}
          />
        </label>
      </div>

      {/* Quick stakes */}
      {!!stakeData?.stakeSet?.length && (
        <div className="grid grid-cols-4 gap-1.5 px-4 pt-3">
          {stakeData.stakeSet.map(({ value, label }, index) => (
            <button
              key={`${label}-${index}`}
              type="button"
              onClick={() => setStake((prev) => prev + Number(value))}
              className="h-8 rounded-md border border-shell-border bg-shell text-xs font-semibold text-shell-fg transition hover:border-gold hover:text-gold"
            >
              +{formatNumber(label)}
            </button>
          ))}
        </div>
      )}

      {/* Options */}
      <div className="flex items-center justify-end px-4 pt-3 text-xs">
        {/* <label className="flex cursor-pointer select-none items-center gap-2 text-shell-muted">
          <input
            type="checkbox"
            checked={acceptOddsChange}
            onChange={() => setAcceptOddsChange((value) => !value)}
            className="size-4 accent-gold"
          />
          Accept odds change
        </label> */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setStake(0)}
            className="text-shell-muted hover:text-shell-fg"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => dispatch(setEditStake(true))}
            className="flex items-center gap-1 text-gold hover:underline"
          >
            Edit stakes
          </button>
        </div>
      </div>

      {/* Runner exposure preview */}
      {!!selectedBet.runners?.length && (
        <div className="mx-4 mt-3 space-y-1 rounded-lg bg-shell px-3 py-2 text-xs">
          {selectedBet.runners.map((runner) => {
            const exposure = getBooksetBySelectionId(runner?.selectionId);
            return (
              <div key={runner.selectionId} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-shell-muted">
                  {runner.runnerName}
                </span>
                <span className={exposureClass(exposure?.apiExposure)}>
                  {exposure?.apiExposure ? formatNumber(exposure.apiExposure) : ''}
                </span>
                {!!exposure?.deltaExposure && (
                  <span className={`font-semibold ${exposureClass(exposure.totalExposure)}`}>
                    » {formatNumber(exposure.totalExposure)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-[1fr_2fr] gap-2 p-4">
        <button
          type="button"
          onClick={reset}
          className="h-11 rounded-lg border border-shell-border text-sm font-semibold text-shell-fg transition hover:border-danger-red hover:text-danger-red"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={isLoading || !stake}
          onClick={handleBetPlace}
          className="flex h-11 items-center justify-center gap-2 rounded-lg bg-gold text-sm font-bold text-gold-foreground transition hover:bg-gold-hover disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <span className="size-4 animate-spin rounded-full border-2 border-gold-foreground border-t-transparent" />
              Placing...
            </>
          ) : (
            <>
              <Icons.SignIn className="size-3.5" />
              Place bet
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BetSlipForm;
